import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  GraduationCap, Loader2, Plus, X, Pencil, Trash2, Image as ImageIcon,
  Building2, Users, CheckCircle2, ExternalLink,
} from "lucide-react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import ConfirmModal from "../../components/ConfirmModal";
import { CourseCover, invalidateCover } from "../../components/CourseCover";
import { FormazioneReportPanel } from "./FormazioneReport";

/**
 * Super admin — Catalogo formazione: corsi SCORM multipli con categoria,
 * descrizione, copertina e assegnazione per tenant.
 */

const fmtKb = (b) => `${Math.round((b || 0) / 1024)} KB`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");

// ─── Modale creazione corso ─────────────────────────────────────────
const CreateCourseModal = ({ categories, onClose, onCreated }) => {
  const [courseType, setCourseType] = useState("scorm_zip");
  const [zipFile, setZipFile] = useState(null);
  const [externalUrl, setExternalUrl] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const isLink = courseType === "link_esterno";

  const submit = async () => {
    if (isLink) {
      if (!title.trim()) { toast.error("Il titolo è obbligatorio per un corso da link esterno"); return; }
      if (!/^https?:\/\//.test(externalUrl.trim())) { toast.error("Inserisci un URL valido (http:// o https://)"); return; }
    } else {
      if (!zipFile) { toast.error("Seleziona il pacchetto SCORM (.zip)"); return; }
      if (!zipFile.name.toLowerCase().endsWith(".zip")) { toast.error("Il file deve essere un archivio .zip"); return; }
    }
    setSaving(true);
    try {
      let data;
      if (isLink) {
        ({ data } = await api.post("/superadmin/formazione/courses/link", {
          title, category, description, external_url: externalUrl.trim(),
        }));
      } else {
        const fd = new FormData();
        fd.append("file", zipFile);
        fd.append("title", title);
        fd.append("category", category);
        fd.append("description", description);
        ({ data } = await api.post("/superadmin/formazione/courses", fd,
          { headers: { "Content-Type": "multipart/form-data" } }));
      }
      if (coverFile) {
        const cf = new FormData();
        cf.append("file", coverFile);
        await api.post(`/superadmin/formazione/courses/${data.id}/cover`, cf,
          { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`Corso «${data.title}» creato. Ora assegnalo ai tenant.`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella creazione del corso");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-modal-overlay" onClick={() => !saving && onClose()}>
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="wl-modal__header">
          <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"><GraduationCap size={18} /></div>
          <div className="wl-modal__title-block">
            <h3 className="wl-modal__title">Nuovo corso</h3>
            <p className="wl-modal__desc">Pacchetto SCORM 1.2 (.zip) con <code>imsmanifest.xml</code> alla root (max 100 MB), oppure un link a un corso ospitato su un sito esterno.</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} className="text-text-muted hover:text-text-primary" aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="wl-modal__body space-y-3">
          <div className="wl-field">
            <label className="wl-label">Tipo di corso</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setCourseType("scorm_zip")} disabled={saving}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${!isLink ? "border-accent bg-accent-subtle font-medium" : "border-border"}`}
                      style={{ color: !isLink ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                      data-testid="course-type-zip">
                Pacchetto SCORM (.zip)
              </button>
              <button type="button" onClick={() => setCourseType("link_esterno")} disabled={saving}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${isLink ? "border-accent bg-accent-subtle font-medium" : "border-border"}`}
                      style={{ color: isLink ? "var(--wl-ink)" : "var(--wl-text-secondary)" }}
                      data-testid="course-type-link">
                <ExternalLink size={11} style={{ display: "inline", verticalAlign: "-1px", marginRight: 4 }} />
                Link esterno
              </button>
            </div>
          </div>
          {isLink ? (
            <div className="wl-field">
              <label className="wl-label">URL del corso *</label>
              <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} maxLength={500}
                     placeholder="https://… (es. corso ospitato su Coursebox)"
                     className="wl-input" data-testid="course-external-url-input" />
              <span className="wl-helper">Il corso si apre in un riquadro dentro Wattline, ma i progressi (punteggio, completamento) non possono essere tracciati.</span>
            </div>
          ) : (
            <div className="wl-field">
              <label className="wl-label">Pacchetto SCORM (.zip) *</label>
              <input type="file" accept=".zip,application/zip" disabled={saving}
                     onChange={(e) => setZipFile(e.target.files?.[0] || null)}
                     className="wl-input" style={{ padding: "8px 12px", height: "auto" }}
                     data-testid="course-zip-input" />
            </div>
          )}
          <div className="wl-field">
            <label className="wl-label">Titolo{isLink ? " *" : ""}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                   placeholder={isLink ? "es. Formazione back office (Coursebox)" : "Se vuoto, uso il titolo del manifest SCORM"}
                   className="wl-input" data-testid="course-title-input" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Categoria</label>
            <input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60}
                   placeholder="es. Back Office, Sicurezza, Onboarding…" list="course-categories"
                   className="wl-input" data-testid="course-category-input" />
            <datalist id="course-categories">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          <div className="wl-field">
            <label className="wl-label">Descrizione</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
                      rows={3} placeholder="Cosa impara chi segue questo corso…"
                      className="wl-textarea" data-testid="course-description-input" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Copertina (JPG/PNG, max 2 MB)</label>
            <input type="file" accept=".jpg,.jpeg,.png" disabled={saving}
                   onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                   className="wl-input" style={{ padding: "8px 12px", height: "auto" }}
                   data-testid="course-cover-input" />
          </div>
        </div>
        <div className="wl-modal__actions">
          <button type="button" onClick={onClose} disabled={saving} className="wl-btn wl-btn--ghost">Annulla</button>
          <button type="button" onClick={submit}
                  disabled={saving || (isLink ? !externalUrl.trim() || !title.trim() : !zipFile)}
                  className="wl-btn wl-btn--primary" data-testid="course-create-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Crea corso
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modale modifica + assegnazione ─────────────────────────────────
const EditCourseModal = ({ course, orgs, categories, onClose, onSaved }) => {
  const [title, setTitle] = useState(course.title || "");
  const [category, setCategory] = useState(course.category || "");
  const [description, setDescription] = useState(course.description || "");
  const [externalUrl, setExternalUrl] = useState(course.external_url || "");
  const [orgIds, setOrgIds] = useState(new Set(course.org_ids || []));
  const [coverFile, setCoverFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isLink = course.course_type === "link_esterno";

  const toggleOrg = (id) => setOrgIds((s) => {
    const c = new Set(s);
    if (c.has(id)) c.delete(id); else c.add(id);
    return c;
  });

  const submit = async () => {
    if (!title.trim()) { toast.error("Il titolo non può essere vuoto"); return; }
    if (isLink && !/^https?:\/\//.test(externalUrl.trim())) { toast.error("Inserisci un URL valido (http:// o https://)"); return; }
    setSaving(true);
    try {
      await api.patch(`/superadmin/formazione/courses/${course.id}`, {
        title, category, description, org_ids: Array.from(orgIds),
        ...(isLink ? { external_url: externalUrl.trim() } : {}),
      });
      if (coverFile) {
        const cf = new FormData();
        cf.append("file", coverFile);
        await api.post(`/superadmin/formazione/courses/${course.id}/cover`, cf,
          { headers: { "Content-Type": "multipart/form-data" } });
        invalidateCover(course.id);
      }
      toast.success(`Corso «${title}» aggiornato`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wl-modal-overlay" onClick={() => !saving && onClose()}>
      <div className="wl-modal wl-modal--md" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="wl-modal__header">
          <div className="wl-modal__icon wl-modal__icon--info" aria-hidden="true"><Pencil size={16} /></div>
          <div className="wl-modal__title-block">
            <h3 className="wl-modal__title">Modifica corso</h3>
            <p className="wl-modal__desc">Metadati, copertina e tenant assegnati.</p>
          </div>
          <button type="button" onClick={() => !saving && onClose()} className="text-text-muted hover:text-text-primary" aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="wl-modal__body space-y-3" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <div className="wl-field">
            <label className="wl-label">Titolo *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
                   className="wl-input" data-testid="course-edit-title" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Categoria</label>
            <input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={60}
                   list="course-categories-edit" className="wl-input" data-testid="course-edit-category" />
            <datalist id="course-categories-edit">
              {categories.map((c) => <option key={c} value={c} />)}
            </datalist>
          </div>
          {isLink && (
            <div className="wl-field">
              <label className="wl-label">URL del corso *</label>
              <input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} maxLength={500}
                     className="wl-input" data-testid="course-edit-external-url" />
            </div>
          )}
          <div className="wl-field">
            <label className="wl-label">Descrizione</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500}
                      rows={3} className="wl-textarea" data-testid="course-edit-description" />
          </div>
          <div className="wl-field">
            <label className="wl-label">{course.has_cover ? "Sostituisci copertina" : "Copertina"} (JPG/PNG, max 2 MB)</label>
            <input type="file" accept=".jpg,.jpeg,.png" disabled={saving}
                   onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                   className="wl-input" style={{ padding: "8px 12px", height: "auto" }}
                   data-testid="course-edit-cover" />
          </div>
          <div className="wl-field">
            <label className="wl-label">Tenant assegnati</label>
            <div className="rounded-lg border border-border divide-y divide-border max-h-44 overflow-y-auto">
              {orgs.map((o) => (
                <label key={o.org_id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-subtle transition-colors"
                       data-testid={`course-assign-${o.org_id}`}>
                  <input type="checkbox" checked={orgIds.has(o.org_id)} onChange={() => toggleOrg(o.org_id)}
                         className="w-4 h-4" />
                  <div className="min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--wl-ink)" }}>{o.org_name}</div>
                    <div className="text-[11px]" style={{ color: "var(--wl-text-muted)" }}>{o.org_id}</div>
                  </div>
                </label>
              ))}
            </div>
            <span className="wl-helper">Gli utenti dei tenant selezionati vedranno il corso nella loro pagina Formazione (se il modulo è attivo).</span>
          </div>
        </div>
        <div className="wl-modal__actions">
          <button type="button" onClick={onClose} disabled={saving} className="wl-btn wl-btn--ghost">Annulla</button>
          <button type="button" onClick={submit} disabled={saving}
                  className="wl-btn wl-btn--primary" data-testid="course-edit-submit">
            {saving && <Loader2 size={14} className="animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Richieste formazione (percorsi su misura) ──────────────────────
const URGENCY_LABELS = { alta: "Il prima possibile", media: "Entro un mese", bassa: "Senza fretta" };
const REQ_STATUS = {
  new: { label: "Nuova", cls: "wl-badge wl-badge--warning" },
  in_progress: { label: "In lavorazione", cls: "wl-badge wl-badge--info" },
  delivered: { label: "Consegnata", cls: "wl-badge wl-badge--success" },
};

const TrainingRequestsPanel = () => {
  const [rows, setRows] = useState(null);
  const [orgFilter, setOrgFilter] = useState("");

  const [reqFailed, setReqFailed] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/superadmin/formazione/requests");
      setRows(data.rows || []);
      setReqFailed(false);
    } catch (e) { setReqFailed(true); setRows((r) => r || []); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    try {
      await api.patch(`/superadmin/formazione/requests/${r.id}`, { status });
      toast.success(status === "delivered" ? "Richiesta segnata come consegnata" : "Richiesta presa in carico");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  if (rows === null) return null;
  const orgs = [...new Map(rows.map((r) => [r.org_id, r.org_name])).entries()];
  const visible = rows.filter((r) => !orgFilter || r.org_id === orgFilter);
  const nNew = rows.filter((r) => r.status === "new").length;

  return (
    <div className="mt-10" data-testid="training-requests-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--wl-ink)" }}>
          Richieste formazione
          {nNew > 0 && (
            <span className="wl-badge wl-badge--warning" style={{ fontSize: "10px" }} data-testid="training-requests-new-count">
              {nNew} {nNew === 1 ? "nuova" : "nuove"}
            </span>
          )}
        </div>
        {orgs.length > 1 && (
          <select value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} className="wl-input"
                  style={{ width: "auto", height: "34px", fontSize: "12px" }}
                  data-testid="training-requests-org-filter">
            <option value="">Tutti i tenant</option>
            {orgs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
      </div>
      {reqFailed ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox compact what="le richieste dei tenant" onRetry={load} testId="training-requests-error" />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-6 text-center text-xs"
             style={{ color: "var(--wl-text-secondary)" }} data-testid="training-requests-empty">
          Nessuna richiesta di percorso formativo dai tenant.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((r) => {
            const st = REQ_STATUS[r.status] || REQ_STATUS.new;
            return (
              <div key={r.id} className="bg-surface border border-border rounded-xl p-4"
                   data-testid={`training-request-row-${r.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold" style={{ color: "var(--wl-ink)" }}>{r.area}</span>
                      <span className={st.cls} style={{ fontSize: "10px" }}>{st.label}</span>
                      <span className="wl-badge wl-badge--neutral" style={{ fontSize: "10px" }}>
                        <Building2 size={9} /> {r.org_name}
                      </span>
                    </div>
                    {r.goal && (
                      <div className="text-xs mt-1.5 leading-relaxed" style={{ color: "var(--wl-text-secondary)" }}>
                        {r.goal}
                      </div>
                    )}
                    <div className="text-[11px] mt-1.5 flex items-center gap-3 flex-wrap" style={{ color: "var(--wl-text-muted)" }}>
                      {r.audience && <span>Destinatari: {r.audience}</span>}
                      <span>Urgenza: {URGENCY_LABELS[r.urgency] || r.urgency}</span>
                      <span>{r.user_name} · {fmtDate(r.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status === "new" && (
                      <button type="button" onClick={() => setStatus(r, "in_progress")}
                              className="wl-btn wl-btn--ghost wl-btn--sm" style={{ border: "1px solid var(--wl-border)" }}
                              data-testid={`training-request-take-${r.id}`}>
                        Prendi in carico
                      </button>
                    )}
                    {r.status !== "delivered" && (
                      <button type="button" onClick={() => setStatus(r, "delivered")}
                              className="wl-btn wl-btn--primary wl-btn--sm"
                              data-testid={`training-request-deliver-${r.id}`}>
                        Segna consegnata
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Pagina ─────────────────────────────────────────────────────────
export default function FormazioneCatalog() {
  const [courses, setCourses] = useState(null);
  const [categories, setCategories] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [catFailed, setCatFailed] = useState(false);
  const load = async () => {
    try {
      const [{ data }, { data: tf }] = await Promise.all([
        api.get("/superadmin/formazione/courses"),
        api.get("/superadmin/tenant-features"),
      ]);
      setCourses(data.courses || []);
      setCategories(data.categories || []);
      setOrgs((tf.rows || []).map((r) => ({ org_id: r.org_id, org_name: r.org_name })));
      setCatFailed(false);
    } catch (e) {
      setCatFailed(true);
      setCourses((c) => c || []);
    }
  };
  useEffect(() => { load(); }, []);

  const runDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/superadmin/formazione/courses/${deleting.id}`);
      toast.success(`Corso «${deleting.title}» eliminato`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'eliminazione");
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="wl-page">
      <div className="wl-page__header flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="wl-page__eyebrow">
            <GraduationCap size={12} strokeWidth={2} style={{ marginRight: 4, verticalAlign: "-1px" }} />
            Amministrazione Wattline
          </div>
          <h1 className="wl-page__title">Catalogo formazione</h1>
          <p className="wl-page__meta">
            Corsi SCORM 1.2 con categoria, copertina e assegnazione per tenant. I progressi degli utenti sono tracciati per corso.
          </p>
        </div>
        <button type="button" onClick={() => setShowCreate(true)} className="wl-btn wl-btn--primary"
                data-testid="new-course-btn">
          <Plus size={14} /> Nuovo corso
        </button>
      </div>

      {courses === null ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--wl-text-secondary)" }}>
          <Loader2 size={14} className="animate-spin" /> Carico il catalogo…
        </div>
      ) : catFailed ? (
        <div className="bg-surface border border-border rounded-xl">
          <LoadErrorBox what="il catalogo corsi" onRetry={load} testId="sa-catalog-error" />
        </div>
      ) : courses.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center" data-testid="catalog-empty">
          <GraduationCap size={32} strokeWidth={1.5} className="mx-auto mb-3" style={{ color: "var(--wl-text-muted)" }} />
          <div className="text-sm font-medium" style={{ color: "var(--wl-ink)" }}>Nessun corso nel catalogo.</div>
          <div className="text-xs mt-1" style={{ color: "var(--wl-text-secondary)" }}>Carica il primo pacchetto SCORM con «Nuovo corso».</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="sa-courses-grid">
          {courses.map((c) => (
            <div key={c.id} className="bg-surface border border-border rounded-xl overflow-hidden flex flex-col"
                 data-testid={`sa-course-card-${c.id}`}>
              <div className="h-28 overflow-hidden relative">
                <CourseCover courseId={c.id} hasCover={c.has_cover} title={c.title} version={c.updated_at} />
                {c.category && (
                  <span className="absolute top-2 left-2 wl-badge wl-badge--neutral" style={{ fontSize: "10px", background: "rgba(255,255,255,0.92)" }}>
                    {c.category}
                  </span>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="text-sm font-semibold" style={{ color: "var(--wl-ink)" }}>{c.title}</div>
                {c.description && (
                  <div className="text-xs line-clamp-2" style={{ color: "var(--wl-text-secondary)" }}>{c.description}</div>
                )}
                <div className="text-[11px] flex items-center gap-3 flex-wrap" style={{ color: "var(--wl-text-muted)" }}>
                  {c.course_type === "link_esterno" ? (
                    <span className="inline-flex items-center gap-1 min-w-0" data-testid={`sa-course-external-${c.id}`}>
                      <ExternalLink size={11} className="shrink-0" />
                      <span className="truncate" style={{ maxWidth: "220px" }}>{c.external_url}</span>
                    </span>
                  ) : (
                    <span>{c.n_files} file · {fmtKb(c.size_bytes)}</span>
                  )}
                  <span>Caricato {fmtDate(c.uploaded_at)}</span>
                </div>
                {c.course_type === "link_esterno" ? (
                  <div className="text-[11px]" style={{ color: "var(--wl-text-muted)" }}>
                    Corso su sito esterno: progressi non tracciabili
                  </div>
                ) : (
                  <div className="text-[11px] flex items-center gap-3" style={{ color: "var(--wl-text-muted)" }}>
                    <span className="inline-flex items-center gap-1"><Users size={11} /> {c.n_users_started} iniziati</span>
                    <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} /> {c.n_users_completed} completati</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 flex-wrap mt-1" data-testid={`sa-course-orgs-${c.id}`}>
                  {(c.orgs || []).length === 0 ? (
                    <span className="wl-badge wl-badge--warning" style={{ fontSize: "10px" }}>Nessun tenant assegnato</span>
                  ) : (
                    c.orgs.map((o) => (
                      <span key={o.org_id} className="wl-badge wl-badge--neutral" style={{ fontSize: "10px" }}>
                        <Building2 size={9} /> {o.org_name}
                      </span>
                    ))
                  )}
                </div>
                <div className="flex items-center gap-2 mt-auto pt-2">
                  <button type="button" onClick={() => setEditing(c)}
                          className="wl-btn wl-btn--ghost wl-btn--sm" style={{ border: "1px solid var(--wl-border)" }}
                          data-testid={`sa-course-edit-${c.id}`}>
                    <Pencil size={12} /> Modifica e assegna
                  </button>
                  <button type="button" onClick={() => setDeleting(c)}
                          className="wl-btn wl-btn--ghost wl-btn--sm" style={{ color: "var(--wl-danger-text)" }}
                          data-testid={`sa-course-delete-${c.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <FormazioneReportPanel />

      <TrainingRequestsPanel />

      {showCreate && (
        <CreateCourseModal categories={categories} onClose={() => setShowCreate(false)} onCreated={load} />
      )}
      {editing && (
        <EditCourseModal course={editing} orgs={orgs} categories={categories}
                         onClose={() => setEditing(null)} onSaved={load} />
      )}
      {deleting && (
        <ConfirmModal
          open
          onOpenChange={(o) => { if (!o && !deleteBusy) setDeleting(null); }}
          title={`Eliminare il corso «${deleting.title}»?`}
          description="Il pacchetto e la copertina verranno rimossi definitivamente. I progressi storici degli utenti restano archiviati."
          variant="danger"
          confirmLabel="Elimina corso"
          cancelLabel="Annulla"
          loading={deleteBusy}
          onConfirm={runDelete}
          testId="confirm-delete-course"
        />
      )}
    </div>
  );
}
